"use client";
import { CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./gallery-carousel";
import SlideShowContent from "./slideshow-content";
import Image from "next/image";
import { PlaceholderImage } from "@/components/common/placeholder-image";
import { GalleryItmes } from "../types/common";
import { useLocaleCtx } from "@/contexts/locale";

const CloseIcon = ({ onClick, className }: { onClick: () => void; className: string }) => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("cursor-pointer", className)}
      onClick={onClick}
    >
      <rect width="60" height="60" rx="30" fill="white" />
      <line x1="19.3536" y1="18.5346" x2="41.3539" y2="40.535" stroke="black" />
      <line x1="18.6464" y1="40.7583" x2="40.6468" y2="18.7579" stroke="black" />
    </svg>
  );
};

export default function SlideShow({
  items,
  currentItem,
  close,
}: {
  items: GalleryItmes;
  currentItem: number | null;
  close: () => void;
}) {
  const { lang } = useLocaleCtx();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [thumbAPI, setThumbAPI] = useState<CarouselApi>();

  useEffect(() => {
    if (!api || !(currentItem !== null)) {
      return;
    }
    api.scrollTo(currentItem, true);
  }, [api]);

  const onSelect = useCallback(() => {
    if (!api || !thumbAPI) return;
    setSelectedIndex(api.selectedScrollSnap());
    thumbAPI.scrollTo(api.selectedScrollSnap());
  }, [api, thumbAPI, setSelectedIndex]);

  useEffect(() => {
    if (!api) return;
    onSelect();

    api.on("select", onSelect).on("reInit", onSelect);
  }, [api, onSelect]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!api || !thumbAPI) return;
      api.scrollTo(index);
    },
    [thumbAPI, api]
  );

  const thumbGradientId = "selected";

  return (
    <>
      <Carousel
        opts={{
          align: "start",
          loop: true,
          direction: lang === "en" ? "ltr" : "rtl",
        }}
        className="py-10"
        setApi={setApi}
      >
        <CloseIcon
          onClick={close}
          className="absolute right-2 top-12 z-20 h-auto w-[20px] sm:w-[30px] lg:right-6 lg:top-16 xl:w-[40px]"
        />
        <SlideShowContent items={items} currentItem={currentItem} />
        <CarouselPrevious className="left-2 bg-transparent lg:-left-14 lg:top-1/2" />
        <CarouselNext className="right-2 bg-transparent lg:-right-14 lg:top-1/2" />
      </Carousel>
      <Carousel
        opts={{
          containScroll: "keepSnaps",
          dragFree: true,
          direction: lang === "en" ? "ltr" : "rtl",
        }}
        setApi={setThumbAPI}
        className="lg:max-w-[90vw] xl:max-w-[80vw] 2xl:max-w-[75vw]"
      >
        <CarouselContent className="">
          {items.map((item, index) => {
            return (
              <CarouselItem
                key={item.image_link + index}
                className="basis-12/12 cursor-pointer scroll-smooth"
                onClick={() => onThumbClick(index)}
              >
                <div
                  className={cn(
                    "relative",
                    index !== selectedIndex && "opacity-40",
                    index === selectedIndex &&
                      "h-[100px] w-[140px] rounded-2xl p-2 opacity-100 lg:h-[90px] lg:w-[140px] xl:h-[120px] xl:w-[210px] 2xl:h-[142px] 2xl:w-[245px]",
                    index !== selectedIndex &&
                      "h-[100px] w-[140px] rounded-2xl lg:h-[90px] lg:w-[140px] xl:h-[120px] xl:w-[210px] 2xl:h-[142px] 2xl:w-[245px]"
                  )}
                >
                  {index === selectedIndex && (
                    <svg
                      className="pointer-events-none absolute inset-0 z-0 h-full w-full"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                    >
                      <defs>
                        <linearGradient
                          id={thumbGradientId}
                          x1="840"
                          y1="0"
                          x2="840"
                          y2="436"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#F0DFC1" />
                          <stop offset="0.791452" stopColor="#C89452" />
                          <stop offset="1" stopColor="#B07F44" />
                        </linearGradient>
                      </defs>
                      <rect
                        x="0.5"
                        y="0.5"
                        width="calc(100% - 1px)"
                        height="calc(100% - 1px)"
                        rx="1rem"
                        ry="1rem"
                        fill="none"
                        stroke={`url(#${thumbGradientId})`}
                        strokeWidth="1"
                      />
                    </svg>
                  )}

                  <div className={cn("relative h-full w-full")}>
                    {item.image_link ? (
                      <Image
                        sizes="400px"
                        fill
                        src={item.image_link}
                        alt="thumbnail"
                        className="rounded-2xl"
                      />
                    ) : (
                      <PlaceholderImage />
                    )}
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </>
  );
}
